/**
 * Fiscal Period Manager Component
 * 
 * Manages fiscal periods and period closing
 */

import React, { useState, useEffect } from 'react';
import { fiscalPeriodAPI } from '../services/accountingService';
import { CalendarBlank, PlusCircle, PushPin, LockKey, CheckCircle } from '@phosphor-icons/react';
import DialogModal from './DialogModal';
import '../styles/fiscalPeriodManager.css';

const FiscalPeriodManager = () => {
  const [periods, setPeriods] = useState([]);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [confirmClosePeriodId, setConfirmClosePeriodId] = useState(null);
  const [closeLoading, setCloseLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    period_name: '',
    period_type: 'monthly',
    fiscal_year: new Date().getFullYear(),
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    fetchPeriods();
  }, []);

  const fetchPeriods = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const allPeriods = await fiscalPeriodAPI.getAllPeriods({});
      setPeriods(allPeriods || []);

      // No open period is a normal state for a school that hasn't created one
      // yet — don't let it surface as a page-level error.
      try {
        const current = await fiscalPeriodAPI.getCurrentPeriod();
        setCurrentPeriod(current);
      } catch (_) {
        setCurrentPeriod(null);
      }
    } catch (err) {
      console.error('Error fetching periods:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePeriod = async (e) => {
    e.preventDefault();
    try {
      await fiscalPeriodAPI.createPeriod(formData);
      setFormData({
        period_name: '',
        period_type: 'monthly',
        fiscal_year: new Date().getFullYear(),
        start_date: '',
        end_date: '',
      });
      setShowCreateForm(false);
      fetchPeriods();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLockPeriod = async (periodId) => {
    try {
      await fiscalPeriodAPI.lockPeriod(periodId);
      fetchPeriods();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleClosePeriod = (periodId) => {
    setConfirmClosePeriodId(periodId);
  };

  const confirmClosePeriod = async () => {
    if (!confirmClosePeriodId) return;
    setCloseLoading(true);
    try {
      await fiscalPeriodAPI.closePeriod(confirmClosePeriodId);
      setConfirmClosePeriodId(null);
      fetchPeriods();
    } catch (err) {
      setError(err.message);
    } finally {
      setCloseLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading fiscal periods...</div>;

  return (
    <div className="fiscal-period-manager">
      <div className="header">
        <h2><CalendarBlank size={20} /> Fiscal Period Management</h2>
        <button 
          className="btn-primary"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          <PlusCircle size={16} /> New Period
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showCreateForm && (
        <form onSubmit={handleCreatePeriod} className="create-period-form">
          <h3>Create New Fiscal Period</h3>
          <div className="form-group">
            <label>Period Name</label>
            <input
              type="text"
              value={formData.period_name}
              onChange={(e) => setFormData({...formData, period_name: e.target.value})}
              placeholder="e.g., January 2026"
              required
            />
          </div>
          <div className="form-group">
            <label>Period Type</label>
            <select
              value={formData.period_type}
              onChange={(e) => setFormData({...formData, period_type: e.target.value})}
              required
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                required
              />
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn-primary">Create</button>
            <button 
              type="button" 
              className="btn-secondary"
              onClick={() => setShowCreateForm(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {currentPeriod && (
        <div className="current-period-card">
          <h3><PushPin size={18} /> Current Period</h3>
          <div className="period-info">
            <p><strong>{currentPeriod.period_name}</strong></p>
            <p>{new Date(currentPeriod.start_date).toLocaleDateString()} - {new Date(currentPeriod.end_date).toLocaleDateString()}</p>
            <span className={`status status-${currentPeriod.status}`}>
              {currentPeriod.status}
            </span>
          </div>
        </div>
      )}

      <div className="periods-table">
        <h3>All Periods</h3>
        <table>
          <thead>
            <tr>
              <th>Period Name</th>
              <th>Type</th>
              <th>Fiscal Year</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {periods.map(period => (
              <tr key={period.id} className={`status-${period.status}`}>
                <td>{period.period_name}</td>
                <td>{period.period_type}</td>
                <td>{period.fiscal_year}</td>
                <td>{new Date(period.start_date).toLocaleDateString()}</td>
                <td>{new Date(period.end_date).toLocaleDateString()}</td>
                <td>
                  <span className={`badge badge-${period.status}`}>
                    {period.status}
                  </span>
                </td>
                <td className="actions">
                  {period.status === 'OPEN' && (
                    <>
                      <button 
                        className="btn-small btn-warning"
                        onClick={() => handleLockPeriod(period.id)}
                        title="Lock period (prevent posting)"
                      >
                        <LockKey size={14} /> Lock
                      </button>
                      <button 
                        className="btn-small btn-danger"
                        onClick={() => handleClosePeriod(period.id)}
                        title="Close period and create closing entries"
                      >
                        <CheckCircle size={14} /> Close
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <DialogModal
        isOpen={Boolean(confirmClosePeriodId)}
        onClose={() => setConfirmClosePeriodId(null)}
        title="Close fiscal period"
        message="Close this period? This cannot be undone."
        type="warning"
        primaryAction={{ label: closeLoading ? 'Closing...' : 'Close', onClick: confirmClosePeriod }}
        secondaryAction={{ label: 'Cancel', onClick: () => setConfirmClosePeriodId(null) }}
        showCloseButton={!closeLoading}
        isLoading={closeLoading}
        disableCloseWhenLoading={true}
      />
    </div>
  );
};

export default FiscalPeriodManager;
